/**
 * ConcepteurOnboardingPage — Vincent batch v8 (Eric 2026-05-08)
 *
 * Parcours guidé 5 étapes : Importer vidéo → Réviser transcript → Valider Q&R
 *                            → Générer scripts → Publier
 *
 * Auto-detect des étapes via GET /api/concepteur/onboarding-progress (no DB writes).
 * Confettis à la complétion finale (clic explicite sur étape 5).
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import {
  Upload, FileText, ChatCircle, ChatCircleText, Confetti, CheckCircle, ArrowRight, ArrowsClockwise, Lock,
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";

const STEPS_META = (lang) => [
  {
    id: 1, key: "upload",
    icon: Upload, color: "blue",
    title: lang === "en" ? "Upload your first video" : "Importer ta 1ʳᵉ vidéo",
    desc: lang === "en"
      ? "Pick a recording (MP4 / MOV / MP3) — the AI pipeline will start automatically."
      : "Choisis un enregistrement (MP4 / MOV / MP3) — le pipeline IA démarre tout seul.",
    cta: lang === "en" ? "Open uploader" : "Ouvrir l'importateur",
    hint: lang === "en" ? "Tip: 30–90 minutes recordings work best." : "Astuce : les vidéos de 30 à 90 minutes fonctionnent le mieux.",
  },
  {
    id: 2, key: "transcript",
    icon: FileText, color: "purple",
    title: lang === "en" ? "Review the transcript & speakers" : "Réviser le transcript & locuteurs",
    desc: lang === "en"
      ? "Validate the speaker labels and re-group paragraphs if needed."
      : "Valide les noms des locuteurs et regroupe les paragraphes si besoin.",
    cta: lang === "en" ? "Open transcript" : "Ouvrir le transcript",
    hint: lang === "en" ? "AI auto-detects speakers — just confirm." : "L'IA pré-détecte — il suffit de confirmer.",
  },
  {
    id: 3, key: "qa",
    icon: ChatCircle, color: "emerald",
    title: lang === "en" ? "Validate Q&A" : "Valider les Q&R",
    desc: lang === "en"
      ? "Approve at least one Q&A pair extracted by GPT-5.2 from your video."
      : "Approuve au moins une paire Q&R extraite par GPT-5.2 de ta vidéo.",
    cta: lang === "en" ? "Go to Q&A tab" : "Aller à l'onglet Q&R",
    hint: lang === "en" ? "Each approved Q&A becomes searchable knowledge." : "Chaque Q&R approuvée devient une connaissance recherchable.",
  },
  {
    id: 4, key: "scripts",
    icon: ChatCircleText, color: "amber",
    title: lang === "en" ? "Generate sales scripts" : "Générer les scripts de vente",
    desc: lang === "en"
      ? "Trigger the dedicated AI pipeline to extract conversational scripts (V1/V2/V3)."
      : "Déclenche le pipeline IA dédié pour extraire des scripts conversationnels (V1/V2/V3).",
    cta: lang === "en" ? "Go to Scripts tab" : "Aller à l'onglet Scripts",
    hint: lang === "en" ? "V1=verbatim, V2=rewrite, V3=justified." : "V1=verbatim, V2=reformulé, V3=justifié.",
  },
  {
    id: 5, key: "publish",
    icon: Confetti, color: "rose",
    title: lang === "en" ? "Publish & celebrate" : "Publier & célébrer",
    desc: lang === "en"
      ? "Mark this onboarding as complete. You're now an autonomous Designer!"
      : "Marque cet onboarding comme terminé. Tu es maintenant un Concepteur autonome !",
    cta: lang === "en" ? "Mark as complete" : "Marquer comme terminé",
    hint: lang === "en" ? "Re-do this anytime from your profile." : "Tu peux le refaire à tout moment depuis ton profil.",
  },
];

const COLOR_CLASSES = {
  blue: { ring: "ring-blue-500", bg: "bg-blue-50", text: "text-blue-700", btn: "bg-blue-600 hover:bg-blue-700" },
  purple: { ring: "ring-purple-500", bg: "bg-purple-50", text: "text-purple-700", btn: "bg-purple-600 hover:bg-purple-700" },
  emerald: { ring: "ring-emerald-500", bg: "bg-teal-50", text: "text-teal-700", btn: "bg-teal-600 hover:bg-teal-700" },
  amber: { ring: "ring-amber-500", bg: "bg-amber-50", text: "text-amber-800", btn: "bg-amber-600 hover:bg-amber-700" },
  rose: { ring: "ring-rose-500", bg: "bg-rose-50", text: "text-rose-700", btn: "bg-rose-600 hover:bg-rose-700" },
};

export default function ConcepteurOnboardingPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/concepteur/onboarding-progress`, { withCredentials: true });
      setProgress(data);
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur de chargement" : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh on window focus (so progress updates when user comes back)
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const handleStepCta = (step) => {
    const vid = progress?.first_video?.id;
    if (step.key === "upload") {
      navigate("/dashboard/upload");
    } else if (step.key === "transcript") {
      if (!vid) { toast.warning(lang === "fr" ? "Importe une vidéo d'abord" : "Upload a video first"); return; }
      navigate(`/dashboard/video/${vid}`);
    } else if (step.key === "qa") {
      if (!vid) { toast.warning(lang === "fr" ? "Importe une vidéo d'abord" : "Upload a video first"); return; }
      navigate(`/dashboard/video/${vid}#qa`);
    } else if (step.key === "scripts") {
      if (!vid) { toast.warning(lang === "fr" ? "Importe une vidéo d'abord" : "Upload a video first"); return; }
      navigate(`/dashboard/video/${vid}#scripts`);
    } else if (step.key === "publish") {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (!progress) return;
    const prereqsDone = progress.steps.slice(0, 4).every((s) => s.done);
    if (!prereqsDone) {
      toast.warning(lang === "fr" ? "Terminé les 4 étapes précédentes d'abord" : "Complété the 4 previous steps first");
      return;
    }
    setCompleting(true);
    try {
      await axios.post(`${API}/api/concepteur/onboarding-progress/complete`, {}, { withCredentials: true });
      toast.success(lang === "fr" ? "🎉 Bravo, tu es maintenant Concepteur autonome !" : "🎉 You're now an autonomous Designer!");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-zinc-400" data-testid="concepteur-onboarding-loading">Chargement...</div>;
  }

  const stepsMeta = STEPS_META(lang);
  const stepsState = progress?.steps || [];
  const completedCount = stepsState.filter((s) => s.done).length;
  const totalSteps = stepsState.length || 5;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="concepteur-onboarding-page">
      <div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-zinc-900 tracking-tight" data-testid="onboarding-title">
              {lang === "fr" ? "Onboarding Concepteur" : "Designer onboarding"}
            </h1>
            <p className="text-sm text-zinc-500 mt-1.5">
              {lang === "fr"
                ? `Bienvenue ${user?.name?.split(" ")[0] || ""} ! Suis ces 5 étapes pour devenir un Concepteur autonome chez Knoria.`
                : `Welcome ${user?.name?.split(" ")[0] || ""}! Follow these 5 steps to become an autonomous Knoria Designer.`}
            </p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-600 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-300 rounded-lg"
            data-testid="onboarding-refresh-btn"
            title={lang === "fr" ? "Rafraîchir l'état" : "Refresh state"}
          >
            <ArrowsClockwise size={12} />
            {lang === "fr" ? "Rafraîchir" : "Refresh"}
          </button>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-zinc-500 mb-1.5">
            <span data-testid="onboarding-progress-label">
              {completedCount}/{totalSteps} {lang === "fr" ? "étapes complétées" : "steps completed"}
            </span>
            <span className="font-mono">{Math.round((completedCount / totalSteps) * 100)}%</span>
          </div>
          <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-rose-500 transition-all duration-500 ease-out"
              style={{ width: `${(completedCount / totalSteps) * 100}%` }}
              data-testid="onboarding-progress-bar"
            />
          </div>
        </div>

        {progress?.all_done && (
          <div
            className="mt-4 p-4 rounded-xl border-2 border-rose-200 bg-gradient-to-br from-rose-50 via-amber-50 to-teal-50"
            data-testid="onboarding-complete-banner"
          >
            <div className="flex items-center gap-3">
              <Confetti size={32} weight="duotone" className="text-rose-500" />
              <div>
                <p className="font-bold text-zinc-900">
                  {lang === "fr" ? "🎉 Bravo, tu es Concepteur autonome !" : "🎉 You're an autonomous Designer!"}
                </p>
                <p className="text-xs text-zinc-600">
                  {lang === "fr"
                    ? `Onboarding terminé le ${progress.completed_at ? new Date(progress.completed_at).toLocaleDateString("fr-CA") : ""}.`
                    : `Completed ${progress.completed_at ? new Date(progress.completed_at).toLocaleDateString() : ""}.`}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Steps */}
      <ol className="space-y-3" data-testid="onboarding-steps-list">
        {stepsMeta.map((meta, idx) => {
          const state = stepsState.find((s) => s.id === meta.id) || { done: false };
          const c = COLOR_CLASSES[meta.color];
          const Icon = meta.icon;
          // Step is "locked" if prereqs not done (publish step requires steps 1-4)
          const isLocked = meta.id === 5 && !stepsState.slice(0, 4).every((s) => s.done);

          return (
            <li
              key={meta.id}
              className={`relative bg-white border-2 rounded-xl p-5 transition-all ${
                state.done ? "border-teal-300 ring-1 ring-emerald-100" : isLocked ? "border-zinc-200 opacity-60" : `border-zinc-200 hover:${c.ring} hover:ring-1`
              }`}
              data-testid={`onboarding-step-${meta.id}`}
            >
              <div className="flex items-start gap-4">
                {/* Number circle */}
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 font-bold text-lg ${
                    state.done ? "bg-teal-500 text-white" : isLocked ? "bg-zinc-100 text-zinc-400" : `${c.bg} ${c.text}`
                  }`}
                  data-testid={`onboarding-step-${meta.id}-badge`}
                >
                  {state.done ? <CheckCircle size={24} weight="fill" /> : isLocked ? <Lock size={18} /> : meta.id}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <h3 className={`font-bold text-base ${state.done ? "text-teal-800" : isLocked ? "text-zinc-400" : "text-zinc-900"}`}>
                      <Icon size={18} weight="duotone" className="inline mr-2 -mt-0.5" />
                      {meta.title}
                      {state.done && <span className="ml-2 text-[10px] uppercase font-bold bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">{lang === "fr" ? "Fait" : "Done"}</span>}
                    </h3>
                    {!state.done && !isLocked && (
                      <button
                        onClick={() => handleStepCta(meta)}
                        disabled={completing && meta.id === 5}
                        className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-all ${c.btn} disabled:opacity-50`}
                        data-testid={`onboarding-step-${meta.id}-cta`}
                      >
                        {completing && meta.id === 5 ? "..." : meta.cta}
                        <ArrowRight size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-zinc-600 mt-1">{meta.desc}</p>
                  <p className="text-[11px] text-zinc-400 italic mt-1.5">💡 {meta.hint}</p>

                  {/* Step 1 has a side context: shows current first video if any */}
                  {meta.id === 1 && progress?.first_video && (
                    <div className="mt-2 inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
                      📁 {progress.first_video.title || "Vidéo"} — {progress.first_video.pipeline_status === "complete" ? "✓ Pipeline terminé" : `⏳ ${progress.first_video.pipeline_status || "en attente"}`}
                    </div>
                  )}
                </div>
              </div>

              {/* Connector line between steps (visual continuity) */}
              {idx < stepsMeta.length - 1 && (
                <div className={`absolute left-[34px] -bottom-3 h-3 w-0.5 ${state.done ? "bg-teal-300" : "bg-zinc-200"}`} />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
